<template>
    <div
        :class="sidebarClass"
    >
        <template v-if="!isLoading">
            <template v-for="tab in tabs">
                <div
                    v-if="tab.header"
                    :key="`header-${tab.header}`"
                    class="Sidebar__subsection"
                >
                    <span class="Sidebar__header">
                        {{ tab.header }}
                    </span>
                    <!--
                        TODO: convert to `router-link` or wrapper
                        @event navigate
                     -->
                    <RPNavTab
                        v-for="subTab in getSubTabs(tab)"
                        :key="subTab.label"
                        class="Sidebar__tab"
                        :to="subTab.to"
                        :sub-tabs="getSubTabs(subTab)"
                        :label="subTab.label"
                        :disabled="disabledTabs.includes(subTab.to.name)"
                        :active-tab="activeTab"
                        :dynamic-tab="dynamicTab"
                        @navigate="to => $emit('navigate', to)"
                    />
                </div>
                <div
                    v-else
                    :key="`tab-${tab.label}`"
                    class="Sidebar__section"
                >
                    <RPNavTab
                        class="Sidebar__tab"
                        :label="tab.label"
                        :to="tab.to"
                        :active-tab="activeTab"
                        :disabled="disabledTabs.includes(tab.to.name)"
                        :dynamic-tab="dynamicTab"
                        :sub-tabs="getSubTabs(tab)"
                        @navigate="to => $emit('navigate', to)"
                    />
                </div>
            </template>
        </template>
    </div>
</template>

<script>
import RPNavTab from 'components/Basic/Buttons/RPNavTab.vue';
import stat from 'status/index';
import statusBuilder from 'status/statusBuilder';
import {has} from 'lodash';

export default {
    name: 'RPSidebar',
    components: {
        RPNavTab,
    },
    props: {
        /**
         * A list of tabs to display. See description for more detail
         */
        tabs: {
            type: Array,
            required: true,
        },
        /**
         * The route name for the currently active tab
         */
        activeTab: {
            type: String,
            required: true,
        },
        /**
         * Displays text underneath the currently active tab. Useful to hint when
         * the user has a current subpage/section open
         */
        dynamicTab: {
            type: String,
            default: '',
        },
        /**
         * An array of route names to disable
         */
        disabledTabs: {
            type: Array,
            default: () => ([]),
        },
        status: {
            type: String,
            default: stat.STANDBY,
        },
    },
    computed: {
        isLoading(){
            return this.status === stat.LOADING;
        },
        sidebarClass(){
            return statusBuilder('Sidebar', this.status);
        },
    },
    methods: {
        getSubTabs(tab){
            if(has(tab, 'subTabs')){
                return tab.subTabs;
            }
            if(has(tab, 'subsections')){
                return tab.subsections;
            }
            return [];
        }
    }
};
</script>

<style lang="scss">
    .Sidebar {
        padding: 1rem;
        background: $white;
        border-radius: 4px;
        display: flex;
        flex-direction: column;
        box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.08);

        @include loading(21rem, 15rem);

        &__header {
            margin-left: 0.5rem;
            color: $text-dark;
            font-weight: 600;
        }
        &__section {
            display: flex;
            flex-direction: column;
        }
        &__tab {
            margin-bottom: 0.25rem;
        }
        &__subsection {
            display: flex;
            flex-direction: column;
        }
        &__subsection:not(:last-child) {
            margin-bottom: 1rem;
        }
    }
</style>

<docs>
To display a list of tabs, pass through an array of tab objects. A tab object can be one of two types:

1. Basic Tabs

    These are plain links with no nested tabs underneath them. They require a `label` (text) property and a `to` property
    containing a route object (only the `name` property is required on the route object).

    Ex:

    ```json static
    {
        label: 'First Tab Label',
        to: {
            name: 'SomeRouteName'
        },
    }
    ```

2. Tab with subtabs

    These tabs have a `header` property (text) and a `subTabs` property (array of objects). The `subTabs` array contains
    basic tab objects.

    Ex:

    ```json static
    {
        header: 'First Tab Header',
        subTabs: [
            {
                label: 'A Subtab',
                to: {
                    name: 'SomeSubRoute'
                }
            }
        ]
    }
    ```

```vue
<RPSidebar
    active-tab="SomeRouteName"
    dynamic-tab="Optionally show an active section here"
    :tabs="[
        {
            header: 'First Tab Header',
            subTabs: [
                {
                    label: 'Link One (A Subtab)',
                    to: {
                        name: 'SomeSubRoute'
                    }
                }
            ]
        },
        {
            label: 'Link Two (currently active)',
            to: {
                name: 'SomeRouteName'
            },
        },
        {
            label: 'Link Three',
            to: {
                name: 'AnotherRouteName'
            },
        }
    ]"
>
</RPSidebar>
```
</docs>
